import { useCallback, useEffect, useMemo, useState } from "react";

// react router
import { useParams, useBlocker } from "react-router-dom";

// api calls
import { useSample } from "./../api";

import { useUserContext } from "../contexts";

// components
import SampleInformation from "../components/ViewSample/SampleInformation";
import SampleResults from "../components/ViewSample/SampleResults";
import SampleImageManager from "../components/ViewSample/SampleImageManager";
import OtherResults from "../components/ViewSample/OtherResults";
import PostConsumption from "../components/ViewSample/PostConsumption";
import Notes from "../components/ViewSample/Notes";

import Footer from "./../components/Footer";
import Navbar from "../components/SpectraNavbar";
import { CardA } from "./../components/ui/Cards";

export default function ViewSample() {
  const { user } = useUserContext();
  const customerType = user?.customerType;
  const { id } = useParams();
  const [dirtyState, setDirtyState] = useState({
    sampleInformation: false,
    postConsumption: false,
    notes: false,
    sampleImages: false,
  });

  const hasUnsavedChanges = useMemo(
    () => Object.values(dirtyState).some(Boolean),
    [dirtyState],
  );

  const handleSampleInformationDirtyChange = useCallback((isDirty) => {
    setDirtyState((prev) => {
      if (prev.sampleInformation === isDirty) {
        return prev;
      }

      return {
        ...prev,
        sampleInformation: isDirty,
      };
    });
  }, []);

  const handlePostConsumptionDirtyChange = useCallback((isDirty) => {
    setDirtyState((prev) => {
      if (prev.postConsumption === isDirty) {
        return prev;
      }

      return {
        ...prev,
        postConsumption: isDirty,
      };
    });
  }, []);

  const handleNotesDirtyChange = useCallback((isDirty) => {
    setDirtyState((prev) => {
      if (prev.notes === isDirty) {
        return prev;
      }

      return {
        ...prev,
        notes: isDirty,
      };
    });
  }, []);

  const handleSampleImagesDirtyChange = useCallback((isDirty) => {
    setDirtyState((prev) => {
      if (prev.sampleImages === isDirty) {
        return prev;
      }

      return {
        ...prev,
        sampleImages: isDirty,
      };
    });
  }, []);

  // Block navigation if there are unsaved changes
  useBlocker(({ currentLocation, nextLocation }) => {
    if (
      hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname
    ) {
      return !window.confirm(
        "You have unsaved changes. Are you sure you want to leave this page?",
      );
    }
    return false;
  });

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // api
  const { data: sample, isError, isLoading } = useSample(id);

  if (isError) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <CardA title='Sample not found' />
        </div>
        <Footer />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <div
            className='alert mx-3'
            style={{
              backgroundColor: "#7ECBF7",
              fontWeight: "bold",
            }}
            role='alert'
          >
            Fetching sample ...
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className='flex-container'>
      <Navbar signedIn={false} />
      <div
        className='px-sm-3 px-lg-5 py-3 flex-body'
        style={{ marginTop: "79px" }}
      >
        <CardA>
          <button
            type='button'
            className='back-button'
            onClick={() => {
              window.history.back();
            }}
          >
            <i className='bi bi-arrow-left pe-1'></i>
            Back
          </button>
          <div className='h4'>Results Summary</div>
          <div className='row full-height'>
            <SampleInformation
              sample={sample}
              onDirtyChange={handleSampleInformationDirtyChange}
            />
          </div>
          <div className='row full-height'>
            <SampleResults sample={sample} />
          </div>

          {customerType === "harm-reduction" && (
            <div className='row full-height'>
              <PostConsumption
                id={sample._id}
                postConsumption={sample.postConsumption}
                categories={sample.categories}
                onDirtyChange={handlePostConsumptionDirtyChange}
              />
            </div>
          )}
          <div className='row full-height'>
            <Notes
              id={sample._id}
              sampleNotes={sample.description}
              onDirtyChange={handleNotesDirtyChange}
            />
          </div>
          <div className='row full-height'>
            <OtherResults id={sample._id} otherResults={sample.otherResults} />
          </div>
          <div className='row full-height'>
            <SampleImageManager
              id={sample._id}
              sampleId={sample.sampleId}
              sampleImages={sample.sampleImages || []}
              onDirtyChange={handleSampleImagesDirtyChange}
            />
          </div>
        </CardA>
      </div>
      <Footer />
    </div>
  );
}
